import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  get,
  find,
  isEqual,
  cloneDeep,
  size,
  times,
  filter,
} from "lodash";
import cx from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";

import { DealRoomContext } from "../../../context";
import { DealRoomActionPlanContext } from "../../context";

import Text from "src/components/text";
import AssigneesPopover from "./popovers/recipients";
import AssetsPopover from "./popovers/assets";
import DueDatePopover from "./popovers/dueDate";

import {
  DealRoomAction,
  DealRoomActionEditChanges,
  DealRoomRecipient,
} from "src/types/dealRoom";

import { getActionChanges } from "./stageAction.helper";

import styles from "./styles.module.scss";

const NAME_AUTOSAVE_DELAY_MS = 2000; // action item name only

const StageAction = ({ action, editing, isNewAction, dragging, saving }) => {
  const {
    state,
    handleSaveAction,
    clickCompleteAction,
    clickSaveAction,
    clickDeleteAction,
    showChangeActionSlider,
  } = useContext(DealRoomActionPlanContext);

  const editMode = get(state, "editMode");

  const [draftAction, setDraftAction] = useState<DealRoomAction>(action);

  const prevSavingRef = useRef(saving);
  const prevSaveActionSliderActionIdRef = useRef<string | null>(null);
  const actionItemAutosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftActionRef = useRef<DealRoomAction>(action);
  const prevAssetsRef = useRef(get(action, "assets", []));

  draftActionRef.current = draftAction;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: action.id,
      disabled: !editMode,
      data: {
        action: action,
      },
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const { id: dealRoomId, dealRoom } = useContext(DealRoomContext);

  const {
    id,
    actionItem,
    dueDate,
    assets,
    recipients,
    stage,
    completed,
    canEdit,
  } = draftAction;

  const stageId = stage ?? "";
  const actionId = id ?? "";
  const isCompleted = !!completed;

  const currentUser = useSelector((state) => get(state, "users.me"));

  const canEditAction = useMemo(() => {
    const currentUserId = get(currentUser, "id", "");
    const dealRoomOwnerId = get(dealRoom, "createdBy.id", "");

    const hasDealRoomEditPermission = get(dealRoom, "canEdit", false);
    const isDealRoomOwner =
      currentUserId && dealRoomOwnerId && currentUserId === dealRoomOwnerId;

    return !!(isDealRoomOwner || hasDealRoomEditPermission || canEdit);
  }, [dealRoom, currentUser, canEdit]);

  const canEditInline = editMode && canEditAction && !isCompleted;

  // Reset local draft when switching to a different action row.
  useEffect(() => {
    setDraftAction(action);
    draftActionRef.current = action;
    prevAssetsRef.current = get(action, "assets", []);
  }, [action.id]);

  // Sync draft from the latest server action after save finishes.
  useEffect(() => {
    if (prevSavingRef.current && !saving) {
      setDraftAction(action);
      draftActionRef.current = action;
      prevAssetsRef.current = get(action, "assets", []);
    }
    prevSavingRef.current = saving;
  }, [saving, action]);

  const savingActionId = get(
    state,
    `actions[${stageId}].editAction.savingActionId`,
    null,
  );
  const isNameInputDisabled =
    (isNewAction && saving) ||
    (saving && savingActionId === actionId);

  // Merge reducer/prop updates into draft while not saving.
  // New action: only assets come from reducer (asset slider); other fields stay on local draft.
  useEffect(() => {
    if (saving) {
      return;
    }

    setDraftAction((prev) => {
      if (isNewAction) {
        if (isEqual(get(prev, "assets"), get(action, "assets"))) {
          return prev;
        }

        return { ...prev, assets: get(action, "assets", []) };
      }

      if (isEqual(prev, action)) {
        return prev;
      }

      return { ...prev, ...action };
    });
  }, [action, saving, isNewAction]);

  // Apply inline field edits to the local working copy.
  const updateDraftAction = (changes: DealRoomActionEditChanges) => {
    setDraftAction((prev) => {
      const next = { ...prev, ...changes };
      draftActionRef.current = next;
      return next;
    });
  };

  // Create action, save name directly, or open update slider.
  const saveDraftAction = () => {
    const currentDraft = draftActionRef.current;

    document.body.click();

    if (saving || size(get(currentDraft, "actionItem", "").trim()) === 0) {
      return;
    }

    if (isNewAction) {
      handleSaveAction(currentDraft, stageId, isNewAction);
      return;
    }

    const { changes, stakeholdersToBeNotified } =
      getActionChanges(
        currentDraft,
        get(state, `actions[${stageId}].items`, []),
        id,
      ) ?? {};

    if (size(changes) === 0) {
      return;
    }

    const { actionItem: _actionItem, ...changesForSlider } = cloneDeep(changes);
    const hasSliderChanges = size(changesForSlider) > 0;
    const hasActionItemChange = Object.hasOwn(changes, "actionItem");

    if (hasActionItemChange || isNewAction) {
      handleSaveAction(currentDraft, stageId, isNewAction);
      return;
    }

    if (hasSliderChanges) {
      clickSaveAction(
        currentDraft,
        stageId,
        changesForSlider,
        stakeholdersToBeNotified,
      );
    }
  };

  // Clear pending name autosave timer (effect cleanup).
  const invalidate = () => {
    if (actionItemAutosaveTimerRef.current) {
      clearTimeout(actionItemAutosaveTimerRef.current);
      actionItemAutosaveTimerRef.current = null;
    }
  };

  // Debounce name autosave; resets the timer on each name change.
  const scheduleNameAutosave = (delayMs: number) => {
    if (actionItemAutosaveTimerRef.current) {
      clearTimeout(actionItemAutosaveTimerRef.current);
    }

    actionItemAutosaveTimerRef.current = setTimeout(() => {
      saveDraftAction();
    }, delayMs);
  };

  // Revert draft when the action item update slider is cancelled (INVALIDATE_SAVE_ACTION_SLIDER).
  useEffect(() => {
    const sliderActionId = get(state, "saveActionSlider.action.id", null);

    if (prevSaveActionSliderActionIdRef.current && !sliderActionId) {
      const invalidatedActionId = prevSaveActionSliderActionIdRef.current;

      if (invalidatedActionId === actionId) {
        const savedAction = find(
          get(state, `actions[${stageId}].items`, []),
          (item) => item.id === actionId,
        );

        if (savedAction) {
          setDraftAction(savedAction);
          draftActionRef.current = savedAction;
          prevAssetsRef.current = get(savedAction, "assets", []);
        }
      }
    }

    prevSaveActionSliderActionIdRef.current = sliderActionId;
  }, [state.saveActionSlider, actionId, stageId, state]);

  // Schedule name autosave only; assignees/due date/attachments save on close or confirm.
  useEffect(() => {
    if (
      !canEditInline ||
      saving ||
      showChangeActionSlider
    ) {
      return;
    }

    if (isNewAction) {
      if (size(get(draftAction, "actionItem", "").trim()) === 0) {
        return;
      }

      scheduleNameAutosave(NAME_AUTOSAVE_DELAY_MS);
      return invalidate;
    }

    const { changes } = getActionChanges(
      draftAction,
      get(state, `actions[${stageId}].items`, []),
      id,
    ) ?? {};
    if (size(changes) === 0) {
      return;
    }

    if (Object.hasOwn(changes, "actionItem")) {
      scheduleNameAutosave(NAME_AUTOSAVE_DELAY_MS);
    }

    return invalidate;
  }, [draftAction.actionItem, canEditInline, saving, showChangeActionSlider, stageId, state, actionId, isNewAction]);

  // Save when attachments are confirmed via the assets slider.
  useEffect(() => {
    const nextAssets = get(draftAction, "assets", []);

    if (isEqual(prevAssetsRef.current, nextAssets)) {
      return;
    }

    prevAssetsRef.current = nextAssets;

    if (!canEditInline || saving || showChangeActionSlider) {
      return;
    }

    if (isNewAction && size(get(draftAction, "actionItem", "").trim()) === 0) {
      return;
    }

    const { changes } =
      getActionChanges(
        draftActionRef.current,
        get(state, `actions[${stageId}].items`, []),
        id,
      ) ?? {};

    if (Object.hasOwn(changes ?? {}, "assets")) {
      saveDraftAction();
    }
  }, [draftAction.assets, canEditInline, saving, showChangeActionSlider, stageId, state, id, isNewAction]);

  // Inline edit handlers — update draft; name autosave effect schedules the save.
  const handleActionItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateDraftAction({ actionItem: e.target.value });
  };

  // Save name on blur without waiting for the debounce.
  const handleActionItemBlur = () => {
    if (actionItemAutosaveTimerRef.current) {
      clearTimeout(actionItemAutosaveTimerRef.current);
      actionItemAutosaveTimerRef.current = null;
    }

    if (isNewAction) {
      if (size(get(draftActionRef.current, "actionItem", "").trim()) === 0) {
        return;
      }

      saveDraftAction();
      return;
    }

    const { changes } = getActionChanges(
      draftActionRef.current,
      get(state, `actions[${stageId}].items`, []),
      id,
    ) ?? {};
    if (!Object.hasOwn(changes, "actionItem")) {
      return;
    }

    saveDraftAction();
  };

  const handleDueDateChange = (newDueDate: string) => {
    updateDraftAction({ dueDate: newDueDate });
  };

  const handleDueDatePopoverClose = () => {
    saveDraftAction();
  };

  // Update assignees on the local draft; save when the popover closes.
  const handleRecipientChange = (
    _stageId: string,
    _actionId: string,
    _isNewAction: boolean,
    selectedRecipient: DealRoomRecipient,
    isAdded: boolean,
  ) => {
    const currentRecipients = get(draftActionRef.current, "recipients", []);
    let updatedRecipients = [...currentRecipients];

    if (isAdded) {
      updatedRecipients.push(selectedRecipient);
    } else {
      updatedRecipients = filter(
        updatedRecipients,
        (recipient) =>
          recipient.shareRecipient !== selectedRecipient.shareRecipient,
      );
    }

    updateDraftAction({ recipients: updatedRecipients });
  };

  const handleAssigneesPopoverClose = () => {
    saveDraftAction();
  };

  return (
    <div
      className={cx(styles.action, {
        [styles.dragging]: dragging,
      })}
      style={style}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      key={`action-${actionId}`}
    >
      <div className={styles.leftGroup}>
        {editMode && (
          <i
            className={cx(styles.drag, "paperflite-drag", {
              [styles.disabled]: isNewAction && editing,
            })}
          />
        )}
        {!editMode && <i
          className={cx(styles.checkButton, {
            "paperflite-radio-button-unselected": !isCompleted,
            "fa-solid fa-check-circle": isCompleted,
            [styles.checked]: isCompleted,
            [styles.disabled]: editMode || isNewAction || !canEditAction,
          })}
          onClick={() => {
            if (editMode || isNewAction || !canEditAction) {
              return;
            }

            clickCompleteAction(stageId, actionId, !isCompleted);
          }}
        />}
        {canEditInline ? (
          <input
            value={actionItem ?? ""}
            className={styles.titleInput}
            onChange={handleActionItemNameChange}
            onBlur={handleActionItemBlur}
            placeholder="Add an action item"
            id={`action-${actionId}`}
            name={`action-${actionId}`}
            disabled={isNameInputDisabled}
          />
        ) : (
          <p
            className={cx(styles.title, {
              [styles.active]: isCompleted,
              [styles.disabled]: !canEditAction,
            })}
          >
            <Text className={styles.text} text={isNewAction ? "Add an action item" : actionItem} lines={1} />
          </p>
        )}
      </div>

      <div
        className={cx(styles.rightGroup, {
          [styles.showRightGroup]: true,
          [styles.existingAction]: !isNewAction,
          [styles.showDeleteGroup]: !isNewAction && editMode,
        })}
      >
        {(size(recipients) > 0 || editMode) ? (
          <AssigneesPopover
            dealRoomId={dealRoomId || ""}
            stageId={stageId}
            actionId={actionId}
            selectedRecipients={recipients ?? []}
            onSelectRecipient={handleRecipientChange}
            onPopoverClose={handleAssigneesPopoverClose}
            isNewAction={isNewAction}
            disabled={!canEditInline}
            canEditAction={canEditAction}
            isActionCompleted={isCompleted}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-add-assignee")} />
            <p className={styles.buttonText}>No assignees</p>
          </div>
        )}

        {(dueDate || editMode) ? (
          <DueDatePopover
            dealRoomId={dealRoomId || ""}
            stageId={stageId}
            actionId={actionId}
            isNewAction={isNewAction}
            dueDate={dueDate ?? ""}
            disabled={!canEditInline}
            canEditAction={canEditAction}
            isActionCompleted={isCompleted}
            onDueDateChange={handleDueDateChange}
            onPopoverClose={handleDueDatePopoverClose}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-calendar")} />
            <p className={styles.buttonText}>No due date</p>
          </div>
        )}

        {(size(assets) > 0 || editMode) ? (
          <AssetsPopover
            stageId={stageId}
            actionId={actionId}
            isNewAction={isNewAction}
            selectedAssets={assets ?? []}
            disabled={!canEditInline}
            canEditAction={canEditAction}
            isActionCompleted={isCompleted}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-attachment")} />
            <p className={styles.buttonText}>0 attachments</p>
          </div>
        )}

        {!isNewAction && editMode && (
          <div className={styles.deleteGroup}>
            <i
              className={cx(styles.icon, "paperflite-delete")}
              onClick={() => clickDeleteAction(draftAction, stageId)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StageAction;

export const StageActionLoadingSkeleton = () => {
  return (
    <>
      {times(3, () => (
        <div className={styles.action}>
          <div className={styles.leftGroup}>
            <div className={styles.placeholder}></div>
          </div>
          <div className={styles.rightGroup}>
            {times(3, () => (
              <div className={styles.placeholder}></div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};
